import { createContext, ReactNode, useContext, useState, useMemo, useCallback } from 'react';
import LendingModal, { LendingModalReferrer } from './LendingModal';

interface LendingModalContextType {
  closeModal: () => void;
  isOpen: boolean;
  openModal: ({ referrer }: { referrer: LendingModalReferrer }) => void;
  modalStep: 'contact' | 'goals' | 'schedule' | 'success';
  setModalStep: (step: 'contact' | 'goals' | 'schedule' | 'success') => void;
  hasSelectedDateAndTime: boolean;
  setHasSelectedDateAndTime: (hasSelectedDateAndTime: boolean) => void;
  referrer?: LendingModalReferrer;
}

interface LendingModalProviderProps {
  children: ReactNode;
}

const LendingModalContext = createContext<LendingModalContextType | undefined>(undefined);

export const useLendingModalContext = () => {
  const context = useContext(LendingModalContext);

  if (!context) {
    throw new Error('useLendingModalContext must be used within a LendingModalProvider');
  }

  return context;
};

export const LendingModalProvider = ({ children }: LendingModalProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalStep, setModalStep] = useState<'contact' | 'goals' | 'schedule' | 'success'>('contact');
  const [hasSelectedDateAndTime, setHasSelectedDateAndTime] = useState(false);
  const [referrer, setReferrer] = useState<LendingModalReferrer | undefined>(undefined);

  const openModal = useCallback(({ referrer }: { referrer: LendingModalReferrer }) => {
    setReferrer(referrer);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const contextValue = useMemo(
    () => ({
      closeModal,
      isOpen,
      openModal,
      modalStep,
      setModalStep,
      hasSelectedDateAndTime,
      setHasSelectedDateAndTime,
      referrer,
    }),
    [
      closeModal,
      isOpen,
      openModal,
      modalStep,
      setModalStep,
      hasSelectedDateAndTime,
      setHasSelectedDateAndTime,
      referrer,
    ]
  );

  return <LendingModalContext.Provider value={contextValue}>{children}</LendingModalContext.Provider>;
};

const LendingComponent = () => {
  const { closeModal, isOpen } = useLendingModalContext();

  return <LendingModal isOpen={isOpen} setOpenModal={closeModal} />;
};

export const LendingModalWithContext = ({ children }: { children: ReactNode }) => {
  return (
    <LendingModalProvider>
      <LendingComponent />
      {children}
    </LendingModalProvider>
  );
};
